import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EModalSize,
  EModalVariants,
  ETextStyleVariant,
  EToastVariant,
  Heading as BonfireHeading,
  Modal,
  Radio,
  RadioGroup,
  Text as BonfireText,
  useIsWindowWidthGreaterThan,
} from '@outdoorsyco/bonfire';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToast } from '@/hooks/useToast';
import { flagListing } from '@/services/flagListing';

interface IProps {
  rentalId: number;
  modalIsOpen: boolean;
  setModalIsOpen: (isOpen: boolean) => void;
}

const ReportListingModal: React.FC<IProps> = ({ rentalId, modalIsOpen, setModalIsOpen }) => {
  const intl = useIntl();
  const { showToast } = useToast();
  const desktopView = useIsWindowWidthGreaterThan('sm', true);
  const [selectedOption, setSelectedOption] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const options = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: 'This listing shouldn’t be on Outdoorsy',
          id: 'NB3RIF',
        }),
        description: intl.formatMessage({
          defaultMessage: 'This listing contains false information or may be a fake listing',
          id: 'EiW0Pj',
        }),
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Inappropriate content or spam',
          id: 'sQLz+V',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'The description of this listing contain graphic, promotional, or otherwise offensive content.',
          id: 'j6pn27',
        }),
      },
      {
        title: intl.formatMessage({
          defaultMessage: 'Inappropriate or deceptive photos',
          id: '2rUvBy',
        }),
        description: intl.formatMessage({
          defaultMessage: "The photos of this listing don't match its description.",
          id: '5lW6jL',
        }),
      },
    ],
    [intl],
  );

  const handleReport = async () => {
    setIsLoading(true);
    try {
      await flagListing(rentalId, selectedOption);
      setModalIsOpen(false);
      setSelectedOption('');
      showToast(
        EToastVariant.Success,
        intl.formatMessage({ defaultMessage: 'Success', id: 'xrKHS6' }),
        intl.formatMessage({ defaultMessage: 'Listing reported successfully', id: 'ltVGnO' }),
      );
    } catch {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({ defaultMessage: 'Error', id: 'KN7zKn' }),
        intl.formatMessage({
          defaultMessage: 'Failed to report listing, please try again later',
          id: 'fjZO/n',
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      onClose={() => {
        setModalIsOpen(false);
        setSelectedOption('');
      }}
      size={EModalSize.Medium}
      {...(!desktopView && { variant: EModalVariants.Bottom })}>
      <Modal.Actions />
      <Modal.Header>
        <BonfireHeading variant={EHeadingStyleVariant.H6}>
          <FormattedMessage defaultMessage="Do you want to report this listing?" id="DSkNaX" />
        </BonfireHeading>
        <BonfireText className="mt-2 text-secondary-base" variant={ETextStyleVariant.SmallRegular}>
          <FormattedMessage defaultMessage="If so, please choose a reason:" id="VhCBBG" />
        </BonfireText>
      </Modal.Header>
      <Modal.Content>
        <RadioGroup
          name="report-listing"
          inputValue={selectedOption}
          onChange={value => setSelectedOption(value)}>
          {options.map((option, index) => {
            return (
              <Radio
                key={index}
                ariaLabel={option.title}
                content={{
                  label: option.title,
                  description: (
                    <BonfireText
                      variant={ETextStyleVariant.SmallRegular}
                      className="text-secondary-base">
                      {option.description}
                    </BonfireText>
                  ),
                }}
                value={option.title}
                className="mt-2"
              />
            );
          })}
        </RadioGroup>
      </Modal.Content>
      <Modal.Footer className="flex !justify-end">
        <Button
          label={intl.formatMessage({ defaultMessage: 'Report', id: 'x5Tz6M' })}
          onClick={handleReport}
          variant={EButtonColorVariant.Primary}
          disabled={!selectedOption || isLoading}
          loading={isLoading}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ReportListingModal;
